/**
 * AppFrame — the authenticated chrome around every screen.
 *
 * The navy Sidebar (real, role-gated nav) sits on the left; screen content
 * renders through <Outlet/> on the right. A thin amber banner spans the top when
 * an admin is impersonating a user. Collapsed state persists in localStorage; the
 * narrow extension side-panel defaults to a collapsed icon rail.
 *
 * Role gating + the impersonation banner are UX only — the server + RLS enforce
 * the real boundary. The sidebar consumes the same effective role.
 */
import { useState } from "react";
import { Outlet } from "react-router-dom";
import { useSimulation } from "@/auth/SimulationProvider";
import { isExtension } from "@/platform";
import { Sidebar } from "@/components/Sidebar";

const COLLAPSE_KEY = "tkcc.sidebar.collapsed";

function readCollapsed(defaultValue: boolean): boolean {
  try {
    const raw = window.localStorage.getItem(COLLAPSE_KEY);
    if (raw === null) return defaultValue;
    return raw === "1";
  } catch {
    return defaultValue;
  }
}

export function AppFrame() {
  const { isSimulating, simulatedUser, stopSimulating } = useSimulation();
  // Extension side-panel is narrow → default to the icon rail there.
  const [collapsed, setCollapsed] = useState<boolean>(() => readCollapsed(isExtension()));

  function toggle(): void {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0");
      } catch {
        /* storage unavailable — keep in-memory state only */
      }
      return next;
    });
  }

  return (
    <div className="flex h-full flex-col">
      {isSimulating && simulatedUser && (
        <div className="flex items-center justify-between gap-3 bg-amber-500 px-4 py-1.5 text-sm font-medium text-amber-950">
          <span>
            👤 Viewing as {simulatedUser.full_name ?? simulatedUser.email}{" "}
            <span className="opacity-80">({simulatedUser.role})</span>
          </span>
          <button
            onClick={stopSimulating}
            className="rounded bg-amber-950/15 px-2 py-0.5 text-xs font-semibold transition hover:bg-amber-950/25"
          >
            Exit simulation
          </button>
        </div>
      )}

      <div className="flex min-h-0 flex-1">
        <Sidebar collapsed={collapsed} onToggle={toggle} />
        <main className="flex min-w-0 flex-1 flex-col overflow-auto bg-muted/30">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
